import styled from 'styled-components';
import { NARROW } from '@/breakpoints.js';

// The canvas's own colours (src/viewport.tsx, updateGrid in src/utils/viewport.tsx), so that the
// home page reads as the same place as the app
const CANVAS = '#e0d9d5';
const GRID_LINE = 'rgba(0, 0, 0, 0.1)';
const GROUND_LINE = 'rgba(0, 0, 0, 0.35)';
const GRID_CELL = '48px';

const TITLE_FONT = "'Big Shoulders Display', Inter, system-ui, sans-serif";

export const Page = styled.div`
	min-height: 100vh;
	min-height: 100dvh;
	box-sizing: border-box;
	padding: 1em 1.5em 3em;
	background-color: ${CANVAS};
	background-image:
		linear-gradient(${GRID_LINE} 1px, transparent 1px),
		linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px);
	background-size: ${GRID_CELL} ${GRID_CELL};
	color: #17171a;

	${NARROW} {
		padding: 0.5em 1em 2em;
	}
`;

// The logo leads the page, centred above the heading
export const Top = styled.header`
	padding: 2.5em 0 0;
	display: flex;
	justify-content: center;

	img {
		display: block;
		height: clamp(64px, 11vw, 110px);
	}

	${NARROW} {
		padding-top: 1.5em;
	}
`;

export const Intro = styled.div`
	max-width: 42em;
	margin: 1.75em auto 2.5em;
	text-align: center;

	h1 {
		margin: 0 0 0.25em;
		font-family: ${TITLE_FONT};
		font-weight: 600;
		font-size: clamp(2.4rem, 7vw, 4rem);
		line-height: 1;
	}

	p {
		margin: 0;
		font-size: 1.1rem;
		color: #3b302a;
	}

	${NARROW} {
		margin: 1.25em auto 1.5em;
	}
`;

export const Grid = styled.ul`
	list-style: none;
	margin: 0 auto;
	padding: 0;
	max-width: 1200px;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: 1.25em;

	${NARROW} {
		grid-template-columns: repeat(2, 1fr);
		gap: 0.75em;
	}
`;

// How a card turns towards the pointer, and its item the other way, twice as far, so that the item
// seems to stand out of the card (Stage below)
const CARD_TURN = 'rotateY(10deg) rotateX(5deg)';
// Lifted far enough off the card that a wide item's far edge, turned back towards it, stays in front:
// at 30px a disc or a field reached behind the card, which hid one of its corners
const ITEM_TURN = 'translateZ(64px) rotateY(-20deg) rotateX(-10deg) scale(1.13)';
const TURN_EASING = '0.35s cubic-bezier(0.2, 0.8, 0.2, 1)';

// Only with a pointer that hovers, so that a tap on a phone does not leave a card turned, and not
// for anyone who asks for less motion
const HOVER = '@media (hover: hover) and (prefers-reduced-motion: no-preference)';

export const Card = styled.a`
	display: flex;
	flex-direction: column;
	height: 100%;
	box-sizing: border-box;
	padding: 0.75em 1em 1em;
	border-radius: 0.75em;
	background: #fff;
	color: inherit;
	text-decoration: none;
	box-shadow: 0 0.1em 0.4em #0002;
	/* The item turns in the card's own space, so that it can stand out of it */
	transform-style: preserve-3d;
	transition:
		transform ${TURN_EASING},
		box-shadow ${TURN_EASING};

	${HOVER} {
		&:hover {
			transform: perspective(800px) ${CARD_TURN};
			box-shadow: -0.6em 0.8em 1.6em #0003;
		}
	}

	&:focus-visible {
		outline: 2px solid #17171a;
		outline-offset: 2px;
	}

	${NARROW} {
		padding: 0.5em 0.75em 0.75em;
	}
`;

// On a phone, where two columns leave no room for a card spent on a title, a group's title is a
// banner across the grid
export const GroupItem = styled.li`
	${NARROW} {
		grid-column: 1 / -1;
	}
`;

// A group's title, as a card of its own before the group's datasets: frosted glass, as on iOS,
// so that the canvas's grid shows through it, blurred, and it reads apart from the cards it heads
export const GroupCard = styled.div`
	position: relative;
	height: 100%;
	min-height: 8em;
	box-sizing: border-box;
	padding: 1em;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: 0.15em;
	overflow: hidden;
	border-radius: 0.75em;
	background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.12));
	-webkit-backdrop-filter: blur(16px) saturate(180%);
	backdrop-filter: blur(16px) saturate(180%);
	border: 1px solid rgba(255, 255, 255, 0.55);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.9),
		inset 0 -1px 1px rgba(255, 255, 255, 0.25),
		0 0.5em 1.5em rgba(0, 0, 0, 0.1);

	/* The light caught along the top-left edge */
	&::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: radial-gradient(120% 60% at 0% 0%, rgba(255, 255, 255, 0.55), transparent 60%);
		pointer-events: none;
	}

	h2 {
		position: relative;
		margin: 0;
		font-family: ${TITLE_FONT};
		font-weight: 600;
		font-size: 2.2rem;
		line-height: 1;
	}

	span {
		position: relative;
		font-size: 0.9rem;
		color: #6f5b4b;
	}

	${NARROW} {
		min-height: 0;
		padding: 0.6em 0.9em;
		flex-direction: row;
		align-items: baseline;
		justify-content: space-between;

		h2 {
			font-size: 1.6rem;
		}
	}
`;

// The item stands on a ground line, as on the canvas
// The item stands on a ground line, as on the canvas. Its box fills the stage, and the image keeps
// its proportions inside it, at the bottom, so that every item turns and grows about the same
// point: the middle of where it stands.
export const Stage = styled.div<{ $centred: boolean }>`
	position: relative;
	height: 150px;
	margin: 0.5em 0 0.6em;
	border-bottom: 1px solid ${GROUND_LINE};
	transform-style: preserve-3d;

	img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: contain;
		/* Or in the middle, for a dataset that asks */
		object-position: ${({ $centred }) => ($centred ? 'center' : 'center bottom')};
		transform-origin: ${({ $centred }) => ($centred ? 'center' : 'center bottom')};
		transition: transform ${TURN_EASING};
	}

	${HOVER} {
		${Card}:hover & img {
			transform: ${ITEM_TURN};
		}
	}

	${NARROW} {
		height: 130px;
	}
`;

export const Name = styled.span`
	font-family: ${TITLE_FONT};
	font-weight: 600;
	font-size: 1.5rem;
	line-height: 1.1;

	${NARROW} {
		font-size: 1.25rem;
	}
`;

export const Count = styled.span`
	font-size: 0.9rem;
	color: #6f5b4b;
	font-variant-numeric: tabular-nums;
`;

export const Footer = styled.footer`
	margin-top: 3em;
	text-align: center;
	font-size: 0.85rem;
	color: #6f5b4b;

	p {
		margin: 0;
	}

	/* A link to the contact form, which opens over the page. Only the footer's own: the form's
	   dialog is rendered in the footer too, and its buttons keep their own look. */
	> button {
		margin-top: 0.5em;
		padding: 0.25em 0.5em;
		border: 0;
		background: none;
		color: #17171a;
		font: inherit;
		font-weight: 600;
		text-decoration: underline;
		text-underline-offset: 0.2em;
		cursor: pointer;
	}
`;

export const Status = styled.p`
	text-align: center;
	color: #555;
`;
